import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { IconTooltip } from '../IconTooltip';
export type DatePreset = '24h' | '7d' | '30d' | '90d' | 'ytd' | 'custom';
export interface DateRange {
  preset: DatePreset;
  from: string; // YYYY-MM-DD
  to: string;
}
const TODAY = '2026-09-30';
function shift(days: number) {
  const d = new Date(`${TODAY}T12:00:00`);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
export function rangeForPreset(preset: Exclude<DatePreset, 'custom'>): DateRange {
  const from = preset === '24h' ? shift(0) : preset === '7d' ? shift(6) : preset === '30d' ? shift(29) : preset === '90d' ? shift(89) : '2026-01-01';
  return {
    preset,
    from,
    to: TODAY
  };
}
export const DEFAULT_RANGE = rangeForPreset('30d');

/** Number of days covered by a range (inclusive). */
export function rangeDays(r: DateRange) {
  const ms = new Date(`${r.to}T12:00:00`).getTime() - new Date(`${r.from}T12:00:00`).getTime();
  return Math.max(1, Math.round(ms / 86400000) + 1);
}
interface DateRangePickerProps {
  value: DateRange;
  onChange: (r: DateRange) => void;
}
export function DateRangePicker({
  value,
  onChange
}: DateRangePickerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open) {
      setFrom(value.from);
      setTo(value.to);
    }
  }, [open, value.from, value.to]);
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const fmt = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    day: 'numeric',
    month: 'short'
  });
  const presets: {
    id: Exclude<DatePreset, 'custom'>;
    label: string;
  }[] = [{
    id: '24h',
    label: t('Dernières 24 heures', 'Last 24 hours')
  }, {
    id: '7d',
    label: t('7 derniers jours', 'Last 7 days')
  }, {
    id: '30d',
    label: t('30 derniers jours', 'Last 30 days')
  }, {
    id: '90d',
    label: t('3 derniers mois', 'Last 3 months')
  }, {
    id: 'ytd',
    label: t('Depuis le début de l’année', 'Year to date')
  }];
  const summary = value.preset === 'custom' ? `${fmt(value.from)} – ${fmt(value.to)}` : presets.find(p => p.id === value.preset)?.label ?? '';
  const invalid = !from || !to || from > to;
  const dayUnit = t('J', 'D');
  // Ultra-short label shown in the button itself.
  const shortLabel = value.preset === '24h' ? '24H' : value.preset === '7d' ? `7${dayUnit}` : value.preset === '30d' ? `30${dayUnit}` : value.preset === '90d' ? '3M' : value.preset === 'ytd' ? TODAY.slice(0, 4) : `${rangeDays(value)}${dayUnit}`;
  return <div ref={ref} className="relative flex items-center" data-id="element-5486" data-mp="15mlt5qbpjm0s">
      <IconTooltip label={t(`Période : ${summary}`, `Period: ${summary}`)} data-id="element-5487" data-mp="1tbodx71n1uzi">
        <button type="button" onClick={() => setOpen(v => !v)} aria-label={t(`Choisir la période (${summary})`, `Pick period (${summary})`)} aria-haspopup="dialog" aria-expanded={open} className={`h-7 px-2 inline-flex items-center gap-1.5 rounded-md border text-xs font-semibold tabular-nums transition-colors ${open ? 'bg-white text-[#0F172A] border-slate-300' : 'bg-white border-slate-200 text-slate-600 hover:text-[#0F172A] hover:border-slate-300'}`} data-id="element-5488" data-mp="1yarhen1yzt5d">
          <CalendarDays className="w-3.5 h-3.5" data-id="element-5489" data-mp="sdd16xgf4ynp" />
          {shortLabel}
          {value.preset === 'custom' && <span className="w-1 h-1 rounded-full bg-current -ml-0.5 self-start mt-1.5" aria-hidden="true" data-id="element-5490" data-mp="jerin2ay5jmx" />}
        </button>
      </IconTooltip>

      {open && <div role="dialog" aria-label={t('Période', 'Period')} className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg border border-slate-200 shadow-lg z-[1000] p-2 font-body" data-id="element-5491" data-mp="1c3yhk2ebcb8p">
          <ul className="flex flex-col" data-id="element-5492" data-mp="12cavkh0izmem">
            {presets.map(p => {
          const selected = value.preset === p.id;
          return <li key={p.id} data-id="element-5493" data-mp="1utwr6mxreceg">
                  <button type="button" onClick={() => {
              onChange(rangeForPreset(p.id));
              setOpen(false);
            }} className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-sm text-left text-[#0F172A] hover:bg-slate-50" data-id="element-5494" data-mp="1uuyd5mgbcjay">
                    <span className={selected ? 'font-semibold' : ''} data-id="element-5495" data-mp="27ciutlzgukg4">{p.label}</span>
                    {selected && <Check className="w-4 h-4 text-[#4F46E5]" data-id="element-5496" data-mp="2cc58txn4c4pz" />}
                  </button>
                </li>;
        })}
          </ul>
          <div className="mt-2 pt-3 border-t border-slate-100 flex flex-col gap-2 px-1" data-id="element-5497" data-mp="2gbe1t0ac3ykh">
            <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide" data-id="element-5498" data-mp="2laqkr9y34lhs">
              {t('Personnalisée', 'Custom')}
            </div>
            <div className="grid grid-cols-2 gap-2" data-id="element-5499" data-mp="2v2mop1mur5tx">
              <label className="flex flex-col gap-1 text-xs text-slate-500" data-id="element-5500" data-mp="2v47homtiqgpo">
                {t('Du', 'From')}
                <input type="date" value={from} max={TODAY} onChange={e => setFrom(e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" data-id="element-5501" data-mp="3431ae23qamxy" />
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-500" data-id="element-5502" data-mp="1ds646yfs26dy">
                {t('Au', 'To')}
                <input type="date" value={to} max={TODAY} onChange={e => setTo(e.target.value)} className="h-8 px-2 rounded-md border border-slate-200 text-xs text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" data-id="element-5503" data-mp="18yld7qclqco9" />
              </label>
            </div>
            {from && to && from > to && <p className="text-xs text-red-600 m-0" data-id="element-5504" data-mp="1sm15njdlstf4">
                {t('La date de fin précède la date de début.', 'End date is before start date.')}
              </p>}
            <button type="button" disabled={invalid} onClick={() => {
          onChange({
            preset: 'custom',
            from,
            to
          });
          setOpen(false);
        }} className="h-8 rounded-md bg-[#4F46E5] text-white text-xs font-semibold hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition-colors" data-id="element-5505" data-mp="3uhbo8ejdr0no">
              {t('Appliquer', 'Apply')}
            </button>
          </div>
        </div>}
    </div>;
}